/** @jsx jsx */
import React, { forwardRef, type Ref, useMemo, useRef } from 'react';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { css, jsx } from '@atlaskit/css';
import { HeadingItem, Section } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Checkbox from '@atlaskit/checkbox';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { COMPONENT_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/filters/types.tsx';
import type { Component as ApiComponent } from '@atlassian/jira-portfolio-3-portfolio/src/common/api/types.tsx';
import { SearchField } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/search-field/index.tsx';
import ClearFilterButton from '../common/clear-filter/index.tsx';
import { FilterText } from '../common/filter-text/index.tsx';
import { ContentWrapper, ItemWrapper } from '../common/index.tsx';
import NoMatchFound from '../common/no-match-text/index.tsx';
import TitleWithAvatar from '../common/title-with-avatar/index.tsx';
import TriggerButton from '../common/trigger-button/index.tsx';
import filterMessages from '../messages.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const ComponentFilterTriggerButton = forwardRef(
	(
		props: Pick<Props, 'isOpen' | 'onOpenChange' | 'projects' | 'value'> & TriggerProps,
		ref: Ref<HTMLButtonElement>,
	) => {
		const { isOpen, onOpenChange, projects, value } = props;
		const { formatMessage } = useIntl();

		const filterText = useMemo(() => {
			return projects
				.flatMap(({ components }) => components || [])
				.filter(({ id }) => value.includes(id))
				.map(({ name }) => name)
				.join(', ');
		}, [projects, value]);

		const getAriaLabel = () => {
			// Components, All
			return `${formatMessage(filterMessages[COMPONENT_FILTER_ID])}, ${
				value.length > 0 ? filterText : formatMessage(messages.emptyPlaceholder)
			} ${formatMessage(filterMessages.selected)}`;
		};

		const getTriggerButtonText = () => {
			return value.length > 0 ? (
				<FilterText text={filterText} />
			) : (
				<FormattedMessage {...messages.emptyPlaceholder} />
			);
		};

		return (
			<TriggerButton
				{...props}
				ariaLabel={getAriaLabel()}
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				ref={ref}
				testId="portfolio-3-portfolio.app-simple-plans.top.filter-bar.component-filter.trigger-btn"
				triggerButtonText={getTriggerButtonText()}
			/>
		);
	},
);

const ComponentFilter = ({
	changeFilter,
	clearFilter,
	isOpen,
	onOpenChange,
	onQueryChange,
	projects,
	searchQuery,
	value,
}: Props) => {
	const { formatMessage } = useIntl();
	const triggerButtonRef = useRef<HTMLButtonElement>(null);

	const getFilteredProjects = () => {
		const filteredProjects = [];
		for (const project of projects) {
			const { components = [] } = project;
			const filteredComponents = components.filter(({ name }) =>
				name.toLowerCase().includes(searchQuery.toLowerCase()),
			);
			if (filteredComponents.length > 0) {
				filteredProjects.push({ ...project, components: filteredComponents });
			}
		}

		return filteredProjects;
	};

	const isComponentSelected = (component: number) => value.includes(component);

	const onComponentClick = (clickedComponent: number): void => {
		if (isComponentSelected(clickedComponent)) {
			changeFilter(value.filter((component) => component !== clickedComponent));
		} else {
			changeFilter([...value, clickedComponent]);
		}
	};

	const renderComponents = (components: ApiComponent[]) =>
		components.map(({ id, name }) => (
			<ItemWrapper key={id}>
				<Checkbox
					id={`${id}`}
					isChecked={isComponentSelected(id)}
					key={id}
					label={
						<Tooltip content={name}>
							<div css={componentCheckboxLabelWrapperStyles}>
								<div css={componentCheckboxLabelTitleStyles}>{name}</div>
							</div>
						</Tooltip>
					}
					name={name}
					onChange={() => onComponentClick(id)}
				/>
			</ItemWrapper>
		));

	const renderProjectGroups = () => {
		const filteredProjects = getFilteredProjects();
		return (
			<>
				{filteredProjects.map(({ id, name, key, components, avatarUrl }) => {
					return (
						<Section key={id}>
							<HeadingItem>
								<TitleWithAvatar avatarUrl={avatarUrl} id={id} projectKey={key} name={name} />
							</HeadingItem>
							{renderComponents(components)}
						</Section>
					);
				})}
				{filteredProjects.length === 0 && <NoMatchFound />}
			</>
		);
	};

	return (
		<Popup
			content={(contentProps) => (
				<ContentWrapper>
					<ClearFilterButton isVisible={!!value.length} onClearClick={clearFilter} />
					<SearchField
						ariaLabel={formatMessage(messages.searchComponentsLabel)}
						onQueryChange={onQueryChange}
						placeholder={formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.searchComponentsPlaceholderIssueTermRefresh
								: messages.searchComponentsPlaceholder,
						)}
						searchQuery={searchQuery}
						setInitialFocusRef={contentProps.setInitialFocusRef}
					/>
					{renderProjectGroups()}
				</ContentWrapper>
			)}
			isOpen={isOpen}
			onClose={() => {
				onOpenChange({ isOpen: false });
				triggerButtonRef?.current?.focus();
			}}
			placement="bottom-start"
			shouldUseCaptureOnOutsideClick
			testId="portfolio-3-portfolio.app-simple-plans.top.filter-bar.component-filter"
			trigger={(triggerProps) => {
				const { ref: triggerRef, ...triggerPropsForButton } = triggerProps;
				const mergedRef = mergeRefs(triggerRef, triggerButtonRef);
				return (
					<ComponentFilterTriggerButton
						isOpen={isOpen}
						onOpenChange={onOpenChange}
						projects={projects}
						ref={mergedRef}
						value={value}
						{...triggerPropsForButton}
					/>
				);
			}}
		/>
	);
};

export default ComponentFilter;

const componentCheckboxLabelWrapperStyles = css({
	alignItems: 'center',
	display: 'grid',
	gap: token('space.100'),
	gridTemplateColumns: 'fit-content(100%) fit-content(100%)',
});

const componentCheckboxLabelTitleStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
